import React from 'react';
import { ArrowLeft, ExternalLink, BookOpen, Calendar, Users, Target, TrendingUp, Code, Brain } from 'lucide-react';

interface ProjectDetailProps {
  project: {
    id: string;
    title: string;
    description: string;
    fullDescription?: string;
    tech: string[];
    tags: string[];
    link: string;
    type: string;
    platform?: string;
    progress?: string;
    format?: string;
    challenges?: string[];
    outcomes?: string[];
    timeline?: string;
    team?: string;
    impact?: string;
    technologies?: {
      frontend?: string[];
      backend?: string[];
      database?: string[];
      tools?: string[];
    };
  };
  onBack: () => void;
}

const ProjectDetail: React.FC<ProjectDetailProps> = ({ project, onBack }) => {
  const getProjectDetails = () => {
    switch (project.id) {
      case 'meta-ads-platform':
        return {
          fullDescription: 'A comprehensive advertising API platform that enables developers and advertisers to integrate Meta\'s advertising capabilities directly into their applications. This platform handles millions of API calls daily, providing real-time bidding, campaign management, and analytics.',
          challenges: [
            'Scaling to handle 10M+ daily API requests',
            'Implementing real-time bidding with sub-second latency',
            'Ensuring data consistency across distributed systems',
            'Building comprehensive analytics and reporting'
          ],
          outcomes: [
            'Reduced API response time by 40%',
            'Increased developer adoption by 300%',
            'Achieved 99.9% uptime SLA',
            'Processed $2B+ in ad spend through platform'
          ],
          timeline: 'Q1 2024 - Q4 2024',
          team: '8 engineers, 2 PMs, 1 designer',
          impact: 'Serves 5000+ active developers, processes 10M+ API calls daily',
          technologies: {
            frontend: ['React', 'TypeScript', 'Next.js'],
            backend: ['Node.js', 'GraphQL', 'Redis'],
            database: ['PostgreSQL', 'MongoDB'],
            tools: ['Docker', 'Kubernetes', 'AWS']
          }
        };
      
      case 'analytics-dashboard':
        return {
          fullDescription: 'Enterprise-grade analytics dashboard powered by machine learning algorithms that provide actionable insights from complex datasets. Features real-time data processing, predictive analytics, and customizable visualization components.',
          challenges: [
            'Processing terabytes of data in real-time',
            'Implementing ML models for predictive analytics',
            'Creating intuitive visualizations for complex data',
            'Ensuring data privacy and compliance'
          ],
          outcomes: [
            'Reduced data processing time by 60%',
            'Improved prediction accuracy to 94%',
            'Increased user engagement by 250%',
            'Deployed across 50+ enterprise clients'
          ],
          timeline: 'Q2 2023 - Q1 2024',
          team: '6 engineers, 2 data scientists, 1 PM',
          impact: 'Processes 1TB+ data daily, serves 10,000+ users',
          technologies: {
            frontend: ['React', 'D3.js', 'Material-UI'],
            backend: ['Python', 'TensorFlow', 'FastAPI'],
            database: ['ClickHouse', 'Redis'],
            tools: ['Apache Spark', 'MLflow', 'Grafana']
          }
        };
      
      default:
        return {
          fullDescription: project.description,
          challenges: ['Ongoing development'],
          outcomes: ['In progress'],
          timeline: '2024',
          team: 'Individual',
          impact: 'Personal project',
          technologies: { tools: project.tech }
        };
    }
  };

  const details = getProjectDetails();

  return (
    <div className="min-h-screen bg-gradient-to-br from-beige-50 to-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        {/* Back Button */}
        <button
          onClick={onBack}
          className="inline-flex items-center text-beige-600 hover:text-beige-700 mb-8 transition-colors"
        >
          <ArrowLeft size={20} className="mr-2" />
          Back to Workspace
        </button>

        {/* Project Header */}
        <div className="bg-white rounded-lg shadow-sm p-8 border border-beige-200 mb-8">
          <div className="flex items-start justify-between mb-6">
            <div>
              <h1 className="text-3xl font-light text-gray-900 mb-4">{project.title}</h1>
              <p className="text-lg text-gray-600 mb-4">{details.fullDescription}</p>
              <div className="flex flex-wrap gap-2 mb-6">
                {project.tags.map((tag) => (
                  <span key={tag} className="px-3 py-1 bg-beige-100 text-beige-800 rounded-full text-sm font-medium">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
            <a
              href={project.link}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-4 py-2 bg-beige-600 text-white rounded-lg hover:bg-beige-700 transition-colors"
            >
              {project.type === 'learning' ? 'View Course' : project.type === 'demos' ? 'Try Demo' : 'View Project'}
              <ExternalLink size={16} className="ml-2" />
            </a>
          </div>

          {/* Key Metrics */}
          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <div className="flex items-center space-x-3">
              <Calendar className="text-beige-600" size={20} />
              <div>
                <p className="text-sm text-gray-500">Timeline</p>
                <p className="font-medium text-gray-900">{details.timeline}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Users className="text-beige-600" size={20} />
              <div>
                <p className="text-sm text-gray-500">Team</p>
                <p className="font-medium text-gray-900">{details.team}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <Target className="text-beige-600" size={20} />
              <div>
                <p className="text-sm text-gray-500">Impact</p>
                <p className="font-medium text-gray-900">{details.impact}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Technology Stack */}
        <div className="bg-white rounded-lg shadow-sm p-8 border border-beige-200 mb-8">
          <h2 className="text-2xl font-light text-gray-900 mb-6 flex items-center">
            <Code className="mr-3 text-beige-600" />
            Technology Stack
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {Object.entries(details.technologies).map(([category, techs]) => (
              <div key={category}>
                <h3 className="font-medium text-gray-900 mb-3 capitalize">{category}</h3>
                <div className="space-y-2">
                  {techs?.map((tech: string) => (
                    <div key={tech} className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-beige-400 rounded-full"></div>
                      <span className="text-sm text-gray-600">{tech}</span>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Challenges & Outcomes */}
        <div className="grid md:grid-cols-2 gap-8 mb-8">
          <div className="bg-white rounded-lg shadow-sm p-8 border border-beige-200">
            <h2 className="text-2xl font-light text-gray-900 mb-6 flex items-center">
              <Brain className="mr-3 text-beige-600" />
              Challenges
            </h2>
            <ul className="space-y-3">
              {details.challenges.map((challenge, index) => (
                <li key={index} className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-beige-400 rounded-full mt-2 flex-shrink-0"></div>
                  <span className="text-gray-700">{challenge}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-white rounded-lg shadow-sm p-8 border border-beige-200">
            <h2 className="text-2xl font-light text-gray-900 mb-6 flex items-center">
              <TrendingUp className="mr-3 text-beige-600" />
              Outcomes
            </h2>
            <ul className="space-y-3">
              {details.outcomes.map((outcome, index) => (
                <li key={index} className="flex items-start space-x-3">
                  <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                  <span className="text-gray-700">{outcome}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Progress (for learning projects) */}
        {project.type === 'learning' && project.progress && (
          <div className="bg-white rounded-lg shadow-sm p-8 border border-beige-200">
            <h2 className="text-2xl font-light text-gray-900 mb-6 flex items-center">
              <BookOpen className="mr-3 text-beige-600" />
              Learning Progress
            </h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <span className="text-gray-700">Course Completion</span>
                <span className="font-medium text-beige-600">{project.progress}</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div 
                  className="bg-beige-600 h-3 rounded-full transition-all duration-500" 
                  style={{ width: project.progress }}
                ></div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ProjectDetail;
